import { create } from 'zustand';
import { InputStroke, InputPoint, DefaultInputAdapter } from '@eduwrite/shared';

const adapter = new DefaultInputAdapter();

export interface Notification {
    id: string;
    feedback: string;
    overlay?: InputStroke[];
    targetText?: string;
    timestamp: number;
    read: boolean;
}

interface TeacherSuggestion {
    feedback: string;
    overlay?: InputStroke[];
    targetText?: string;
}

const STEADY_STROKE_GRID = 8;

function quantizePoint(point: InputPoint): InputPoint {
    return {
        ...point,
        x: Math.round(point.x / STEADY_STROKE_GRID) * STEADY_STROKE_GRID,
        y: Math.round(point.y / STEADY_STROKE_GRID) * STEADY_STROKE_GRID,
    };
}

interface StrokeState {
    currentStroke: InputStroke | null;
    completedStrokes: InputStroke[];
    structuredData: unknown;
    teacherSuggestion: TeacherSuggestion | null;
    steadyStroke: boolean;

    activeToast: Notification | null;

    startStroke: (point: InputPoint) => void;
    updateStroke: (point: InputPoint) => void;
    endStroke: () => void;
    clearStrokes: () => void;
    setStructuredData: (data: unknown) => void;
    setTeacherSuggestion: (suggestion: TeacherSuggestion | null) => void;
    setToast: (notification: Notification | null) => void;
    setSteadyStroke: (value: boolean) => void;
}

export const useStrokeStore = create<StrokeState>((set) => ({
    currentStroke: null,
    completedStrokes: [],
    structuredData: null,
    teacherSuggestion: null,
    steadyStroke: false,

    activeToast: null,

    startStroke: (point: InputPoint) => set((state) => {
        const quantized = state.steadyStroke ? quantizePoint(point) : point;
        return {
            currentStroke: {
                points: [quantized],
                toolType: 'pen'
            }
        };
        // Do NOT clear teacherSuggestion here, so students can trace over it!
    }),

    updateStroke: (point: InputPoint) => set((state: StrokeState) => {
        if (!state.currentStroke) return state;
        const quantized = state.steadyStroke ? quantizePoint(point) : point;
        return {
            currentStroke: {
                ...state.currentStroke,
                points: [...state.currentStroke.points, quantized]
            }
        };
    }),

    endStroke: () => set((state: StrokeState) => {
        if (!state.currentStroke) return state;

        // Apply Catmull-Rom smoothing before saving, with fallback
        let finalStroke = state.currentStroke;
        try {
            if (state.currentStroke.points && state.currentStroke.points.length >= 2) {
                finalStroke = adapter.smoothStroke(state.currentStroke);
            }
        } catch (e) {
            console.warn('Stroke smoothing failed, using raw stroke:', e);
        }

        // Notify sync service via the registered callback.
        // Using setTimeout so the state update completes before the callback
        // reads the store (the callback is registered by StrokeSyncService
        // via registerOnStrokeEndCallback below — no circular import needed).
        setTimeout(() => _onStrokeEndCallback?.(), 0);

        return {
            completedStrokes: [...state.completedStrokes, finalStroke],
            currentStroke: null
        };
    }),

    clearStrokes: () => set({
        completedStrokes: [],
        currentStroke: null,
        structuredData: null,
        teacherSuggestion: null,
        activeToast: null
    }),

    setStructuredData: (data) => set({ structuredData: data }),

    setTeacherSuggestion: (suggestion) => set({
        teacherSuggestion: suggestion
    }),

    setSteadyStroke: (value: boolean) => set({ steadyStroke: value }),

    setToast: (notification) => set({ activeToast: notification })
}));

// ---------------------------------------------------------------------------
// Callback registration – breaks the circular dependency.
//
// StrokeSyncService imports useStrokeStore (one-way dependency).
// To avoid useStrokeStore importing StrokeSyncService back, we expose a
// registration point. StrokeSyncService calls registerOnStrokeEndCallback()
// during startSync() — well after both modules have fully initialized.
// ---------------------------------------------------------------------------
let _onStrokeEndCallback: (() => void) | null = null;

export function registerOnStrokeEndCallback(cb: () => void): void {
    _onStrokeEndCallback = cb;
}

export function unregisterOnStrokeEndCallback(): void {
    _onStrokeEndCallback = null;
}
